import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { aiApi } from '@/api/ai';
import { patientsApi } from '@/api/patients';
import { testsApi, type Test, type TestAssignment } from '@/api/tests';
import assignInstrumentImg from '@/assets/images/assign-instrument.webp';
import AssignConfirmStep from '@/components/assignments/steps/AssignConfirmStep';
import AssignPatientSelectStep from '@/components/assignments/steps/AssignPatientSelectStep';
import AssignSuccessStep from '@/components/assignments/steps/AssignSuccessStep';
import { SubscriptionPlansModal } from '@/components/modals/SubscriptionPlansModal';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { AppDialogLayout } from '@/components/system';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { useModalContext } from '@/components/ui/modal-provider';
import { useDebounce } from '@/hooks/use-debounce';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import {
    EntitlementBlockedError,
    isEntitlementBlockedError,
} from '@/lib/entitlement-blocked-error';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { TestStatusBadge } from '@/pages/patients/components/StatusBadges';
import { getSharableTestText } from '@/pages/tests/utils/getTestText';
import { Patient } from '@/types/patient';
import type { AssignPatientListItem } from '@/components/assignments/steps/AssignPatientSelectStep';

const ASSIGN_INSTRUMENT_IMAGE = {
    src: assignInstrumentImg,
    width: 900,
    height: 725,
};

interface TestAssignWizardProps {
    test: Test;
    preSelectedPatient?: Patient;
    onClose: () => void;
    allowPatientSelection?: boolean;
    onAssigned?: (testId: string) => void;
    fromReport?: boolean;
}

type ActiveAssignmentConflictResponse = {
    reason?: string;
};

export default function TestAssignWizard({
    test,
    preSelectedPatient,
    onClose,
    allowPatientSelection = true,
    onAssigned,
    fromReport = false,
}: TestAssignWizardProps) {
    const queryClient = useQueryClient();
    const { closeModal, openModal } = useModalContext();
    const assignmentStatusesQueryKey = ['tests', test.id, 'patient-assignment-statuses'] as const;
    const [step, setStep] = useState<1 | 2 | 3>(
        preSelectedPatient || !allowPatientSelection ? 2 : 1,
    );
    const [selectedPatient, setSelectedPatient] = useState<Patient | null>(
        preSelectedPatient || null,
    );
    const [searchQuery, setSearchQuery] = useState('');
    const [assignment, setAssignment] = useState<TestAssignment | null>(null);
    const debouncedSearchQuery = useDebounce(searchQuery, 300);
    const normalizedSearchQuery = debouncedSearchQuery.trim();
    const sendTestAccess = useFeatureEntitlement('tests.send');
    const correctWithAiAccess = useFeatureEntitlement('tests.correctWithAi');

    const canSelectPatient = allowPatientSelection;

    const {
        data: patients = [],
        isLoading: isLoadingPatients,
        isFetching: isFetchingPatients,
    } = useQuery({
        queryKey: ['patients', 'all', normalizedSearchQuery],
        queryFn: () => patientsApi.listAll({ q: normalizedSearchQuery || undefined }),
        enabled: canSelectPatient,
    });

    const { data: assignmentStatuses = [], isLoading: isLoadingAssignmentStatuses } = useQuery({
        queryKey: assignmentStatusesQueryKey,
        queryFn: () => testsApi.listPatientAssignmentStatuses(test.id),
        enabled: Boolean(test.id),
    });

    const { data: aiUsage } = useQuery({
        queryKey: ['ai-usage'],
        queryFn: aiApi.getUsage,
        staleTime: 30_000,
    });

    const { mutate: sendTest, isPending } = useMutation({
        mutationFn: async () => {
            if (sendTestAccess.shouldBlock) {
                throw new EntitlementBlockedError(
                    getEntitlementBlockedMessage(sendTestAccess.decision),
                );
            }

            if (!selectedPatient) {
                throw new Error('Paciente não selecionado');
            }
            return await testsApi.send(selectedPatient.id, test.id);
        },
        onSuccess: (data) => {
            setAssignment(data);
            setStep(3);
            if (onAssigned) onAssigned(test.id);
            if (selectedPatient?.id) {
                // Prefix key: the detail page reads assignments from the patient
                // overview, other surfaces from the dedicated tests query.
                queryClient.invalidateQueries({
                    queryKey: ['patients', selectedPatient.id],
                });
            }
            queryClient.invalidateQueries({
                queryKey: assignmentStatusesQueryKey,
            });
            toast.success(`Instrumento atribuído com sucesso para ${selectedPatient?.full_name}`);
        },
        onError: (error: unknown) => {
            const responseData = isAxiosError(error)
                ? (error.response?.data as ActiveAssignmentConflictResponse | undefined)
                : undefined;

            if (
                isAxiosError(error) &&
                error.response?.status === 409 &&
                responseData?.reason === 'active_assignment_exists'
            ) {
                queryClient.invalidateQueries({
                    queryKey: assignmentStatusesQueryKey,
                });

                toast.error('Este paciente já possui um envio ativo deste instrumento.');

                if (canSelectPatient) {
                    setStep(1);
                }
                return;
            }

            if (isEntitlementBlockedError(error) && error.message.trim()) {
                toast.warning(error.message);
                return;
            }

            if (!isAxiosError(error) && error instanceof Error && error.message.trim()) {
                toast.error(error.message);
                return;
            }

            toast.error('Erro ao atribuir instrumento. Tente novamente.');
        },
    });

    const assignmentStatusesByPatientId = useMemo(
        () => new Map(assignmentStatuses.map((status) => [status.patient_id, status])),
        [assignmentStatuses],
    );

    const selectedPatientAssignment = selectedPatient
        ? assignmentStatusesByPatientId.get(selectedPatient.id)
        : undefined;

    const selectablePatients = useMemo<AssignPatientListItem[]>(
        () =>
            patients.map((patient) => {
                const assignmentStatus = assignmentStatusesByPatientId.get(patient.id);

                if (!assignmentStatus) {
                    return { patient };
                }

                return {
                    patient,
                    isSelectable: assignmentStatus.can_assign,
                    badge: (
                        <TestStatusBadge
                            status={assignmentStatus.latest_status}
                            assignmentState={assignmentStatus.assignment_state}
                        />
                    ),
                    helperText:
                        assignmentStatus.assignment_state === 'active'
                            ? 'Já possui um envio ativo deste instrumento.'
                            : assignmentStatus.assignment_state === 'overdue'
                              ? 'Prazo expirado. Pode ser reenviado.'
                              : 'Instrumento concluído anteriormente. Pode ser reenviado.',
                };
            }),
        [assignmentStatusesByPatientId, patients],
    );
    const isLoadingSelectablePatients =
        isLoadingPatients ||
        (isFetchingPatients && patients.length === 0) ||
        isLoadingAssignmentStatuses;

    const handlePatientSelect = (patient: Patient) => {
        setSelectedPatient(patient);
        setStep(2);
    };

    const getDialogTitle = () => {
        if (step === 1) return 'Selecionar paciente';
        if (step === 2) return 'Confirmar envio';
        if (step === 3) return 'Instrumento pronto';
        return '';
    };

    const getFormattedExpiryDate = () => {
        if (!assignment?.unlock_token_expires_at) return '';

        const expiryDate = new Date(assignment.unlock_token_expires_at);

        return new Intl.DateTimeFormat('pt-BR', {
            timeZone: 'America/Sao_Paulo',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(expiryDate);
    };

    const shareLink = assignment
        ? `${window.location.origin}/patients/test/answer?token=${assignment.unlock_token}`
        : '';
    const shareText = shareLink ? getSharableTestText(shareLink) : '';

    const handleBack = () => {
        if (canSelectPatient) {
            setStep(1);
            return;
        }
        onClose();
    };

    const hasActiveAssignment = selectedPatientAssignment?.assignment_state === 'active';
    const isReassignment =
        selectedPatientAssignment?.assignment_state === 'overdue' ||
        selectedPatientAssignment?.assignment_state === 'completed';
    const isAiUsageExhausted =
        (aiUsage?.limit ?? 0) > 0 && (aiUsage?.used ?? 0) >= (aiUsage?.limit ?? 0);
    const isAutomaticConsolidationBlocked = correctWithAiAccess.shouldBlock || isAiUsageExhausted;

    const handleConfirmSend = () => {
        if (sendTestAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(sendTestAccess.decision));
            return;
        }

        sendTest();
    };

    const handleOpenPlansModal = () => {
        openModal({
            title: 'Conheça nossos planos',
            content: (
                <SubscriptionPlansModal
                    close={closeModal}
                    description="Seus créditos de IA acabaram. O paciente pode responder o instrumento normalmente, mas a consolidação automática depende de créditos disponíveis."
                    closeLabel="Fechar"
                />
            ),
            size: 'xl',
        });
    };

    const reassignmentNotice = useMemo(() => {
        if (selectedPatientAssignment?.assignment_state === 'overdue') {
            return (
                <Alert variant="warning">
                    Este paciente já recebeu este instrumento, mas o prazo expirou. Um novo envio
                    será criado e o histórico anterior será mantido.
                </Alert>
            );
        }

        if (selectedPatientAssignment?.assignment_state === 'completed') {
            return (
                <Alert variant="info">
                    Este paciente já concluiu este instrumento anteriormente. Um novo envio será
                    criado e o histórico anterior será mantido.
                </Alert>
            );
        }

        if (hasActiveAssignment) {
            return (
                <Alert variant="warning">
                    Este paciente já possui um envio ativo deste instrumento.
                </Alert>
            );
        }

        return null;
    }, [hasActiveAssignment, selectedPatientAssignment?.assignment_state]);

    const renderContent = () => {
        if (step === 1) {
            return (
                <AssignPatientSelectStep
                    patients={selectablePatients}
                    isLoading={isLoadingSelectablePatients}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    onSelectPatient={handlePatientSelect}
                />
            );
        }

        if (step === 2) {
            return (
                <div className="space-y-4">
                    {reassignmentNotice}
                    {correctWithAiAccess.shouldBlock && (
                        <Alert variant="warning">
                            <div className="space-y-2">
                                <div className="flex items-center justify-between gap-3">
                                    <p>
                                        O paciente pode responder este instrumento normalmente, mas
                                        a consolidação automática não será gerada.
                                    </p>
                                    {correctWithAiAccess.isUnavailableInPlan &&
                                    correctWithAiAccess.availableFromPlan ? (
                                        <FeatureUnavailableChip
                                            availablePlan={correctWithAiAccess.availableFromPlan}
                                        />
                                    ) : null}
                                </div>
                                <p className="text-xs opacity-80">
                                    {getEntitlementBlockedMessage(correctWithAiAccess.decision)}
                                </p>
                            </div>
                        </Alert>
                    )}
                    {!correctWithAiAccess.shouldBlock && isAiUsageExhausted && (
                        <Alert variant="warning">
                            <div className="space-y-2">
                                <p>
                                    O paciente pode responder este instrumento normalmente, mas a
                                    consolidação automática não será gerada enquanto seus créditos
                                    de IA estiverem esgotados.
                                </p>
                                <Button
                                    variant="link"
                                    className="h-auto px-0 text-warning-foreground"
                                    onClick={handleOpenPlansModal}
                                >
                                    Ver planos
                                </Button>
                            </div>
                        </Alert>
                    )}
                    <AssignConfirmStep
                        // Authored casing, not `toTitleCase`: that lowercases the
                        // acronym the name is known by ("(GAD-7)" → "(gad-7)").
                        itemLabel={test.name}
                        itemTypeLabel="instrumento"
                        patientName={selectedPatient?.full_name}
                        image={ASSIGN_INSTRUMENT_IMAGE}
                    />
                </div>
            );
        }

        if (step === 3) {
            return (
                <AssignSuccessStep
                    title="Instrumento atribuído com sucesso!"
                    description={
                        fromReport
                            ? isAutomaticConsolidationBlocked
                                ? 'O link de acesso foi gerado. O paciente pode responder normalmente, mas a consolidação automática não será gerada com o plano ou limite atual.'
                                : 'O link de acesso foi gerado. Quando o paciente concluir o instrumento, a consolidação automática dependerá de créditos de IA disponíveis no momento da geração.'
                            : 'O link de acesso foi gerado e está pronto para compartilhamento.'
                    }
                    expiryDate={getFormattedExpiryDate()}
                    shareLink={shareLink}
                    shareText={shareText}
                />
            );
        }

        return null;
    };

    const content = renderContent();
    const footer = (
        <DialogFooter>
            {step === 1 ? (
                <Button variant="outline" onClick={onClose}>
                    Cancelar
                </Button>
            ) : step === 2 ? (
                /* Back lives in the header, so the footer carries only the
                   action that moves the flow forward. */
                <Button
                    onClick={handleConfirmSend}
                    disabled={isPending || isLoadingAssignmentStatuses || hasActiveAssignment}
                    aria-disabled={sendTestAccess.shouldBlock}
                >
                    {isPending
                        ? 'Gerando...'
                        : isReassignment
                          ? 'Reenviar instrumento'
                          : 'Confirmar envio'}
                    {sendTestAccess.isUnavailableInPlan && sendTestAccess.availableFromPlan ? (
                        <FeatureUnavailableChip availablePlan={sendTestAccess.availableFromPlan} />
                    ) : null}
                </Button>
            ) : (
                <Button onClick={onClose} className="w-full">
                    Fechar
                </Button>
            )}
        </DialogFooter>
    );

    return (
        <AppDialogLayout
            title={getDialogTitle()}
            backAction={step === 2 ? { label: 'Voltar', onClick: handleBack } : undefined}
            footer={footer}
        >
            {content}
        </AppDialogLayout>
    );
}
